import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { APP_CONFIG } from '../config/constants';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Footer from 'components/PageFooter';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, user, isAuthenticated } = useAuth();

  const getRedirectPath = (role?: string) => {
    if (role === 'admin') {
      return '/dashboard';
    }
    if (role === 'client') {
      return '/client-profile';
    }
    return '/image-upload';
  };

  if (isAuthenticated && user) {
    return <Redirect to={getRedirectPath(user.role)} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);

    try {
      await login(username, password);
    } catch (err) {
      setError('Invalid username or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 flex flex-col justify-center px-4 py-8">
        <div className="login-card m-auto w-full max-w-md">
          <div className="card">
            <div className="flex justify-end mb-4">
              <LanguageSwitcher />
            </div>
          
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{APP_CONFIG.appName}</h2>
              <p className="text-gray-600 dark:text-gray-400">{t('auth.login')}</p>
            </div>
          
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  {t('auth.username')}
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-field"
                  placeholder={t('auth.enterUsername')}
                  required
                />
              </div>
            
              <div className="form-group">
                <label className="form-label">
                  {t('auth.password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder={t('auth.enterPassword')}
                  required
                />
              </div>
            
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
            
              <button
                type="submit"
                disabled={isSubmitting}
                className={`btn-primary w-full py-3 text-lg flex items-center justify-center gap-2 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting && (
                  <svg
                    className="w-5 h-5 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                )}
                <span>{isSubmitting ? 'Logging in...' : t('auth.loginButton')}</span>
              </button>
            </form>
          
            <div className="text-center" style={{ marginTop: '1.5rem' }}>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Create test users with different roles via registration
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;